import { useQuery } from '@apollo/client'
import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Mousewheel, Virtual, Pagination } from 'swiper/modules'
import PropTypes from 'prop-types'

import { LIST_REEL_FRONT } from '@graphql/reels/reels.query'
import ReelCard from '../reelCard'
import CustomLink from '@shared/components/customLink'

import styles from './../style.module.scss'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

function ReelSwiper({ isArticle }) {
  const [activeReel, setActiveReel] = useState()

  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const swiperRef = useRef(null)

  // !Reel Data
  const { data } = useQuery(LIST_REEL_FRONT, {
    variables: { input: { bIsSection: true, nLimit: 15 } },
    fetchPolicy: 'network-only'
  })

  const trendingReels = data?.listReelFrontV2?.aData || []

  // !Update Visible Reels
  useEffect(() => {
    if (trendingReels?.length > 0 && swiperRef.current) {
      updateVisibleReels(swiperRef.current)
    }
  }, [trendingReels])

  const updateVisibleReels = (swiper) => {
    if (!swiper || !trendingReels.length) return

    const currentIndex = swiper.activeIndex
    const slidesPerView = swiper.params.slidesPerView

    const actualSlidesPerView =
      typeof slidesPerView === 'string' ? swiper.currentBreakpoint ? swiper.params.breakpoints[swiper.currentBreakpoint].slidesPerView : 5 : slidesPerView

    const visibleSlideIndexes = []
    for (let i = currentIndex; i < currentIndex + actualSlidesPerView && i < trendingReels.length; i++) {
      visibleSlideIndexes.push(i)
    }

    const visible = visibleSlideIndexes?.map((index) => trendingReels[index])

    if (visible?.length > 0) {
      startInterval(visible)
      setActiveReel(visible[0])
    }
  }

  // !Every 10 seconds change reel after visible reels change
  function startInterval(visibleReels) {
    if (visibleReels?.length > 0) {
      let i = 0
      let interval

      const stopInterval = () => {
        if (interval) clearInterval(interval)
      }
      stopInterval()
      const startInterval = () => {
        interval = setInterval(() => {
          i = (i + 1) % visibleReels.length
          const visibleReelId = visibleReels[i]
          if (visibleReelId) {
            setActiveReel(visibleReelId)
          }
        }, 10000) // 10 seconds
      }

      // ? Start interval initially
      startInterval()

      // ? Pause when tab is hidden, resume when visible
      const handleVisibilityChange = () => {
        if (document?.hidden) {
          stopInterval()
        } else {
          startInterval()
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)
      return () => {
        stopInterval()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }

  // !On Slide change update visibleReels
  const handleSlideChange = (swiper) => {
    updateVisibleReels(swiperRef.current)
  }

  return (
    <>
      <div className="position-relative">
        <Swiper
          slidesPerGroup={3}
          slidesPerView={2}
          direction="horizontal"
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current
          }}
          modules={[Navigation, Virtual, Mousewheel, Pagination]}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 4,
            releaseOnEdges: true
          }}
          virtual={{
            enabled: true,
            addSlidesAfter: 2,
            addSlidesBefore: 2
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            // Update navigation after swiper initialization
            if (swiper.params) {
              swiper.params.navigation.prevEl = prevRef.current
              swiper.params.navigation.nextEl = nextRef.current
              swiper.navigation.init()
              swiper.navigation.update()
            }
          }}
          speed={500}
          breakpoints={{
            320: {
              slidesPerView: 3,
              spaceBetween: 5,
              slidesPerGroup: 2
            },
            480: {
              slidesPerView: 3,
              spaceBetween: 11
            },
            768: {
              slidesPerView: 4,
              spaceBetween: 11
            },
            1024: {
              slidesPerView: isArticle ? 6 : 8,
              spaceBetween: 12
            }
          }}
          onSlideChange={handleSlideChange}
          className="shorts-swiper"
        >
          {trendingReels?.map((r, i) => {
            return (
              <SwiperSlide key={r?._id}>
                <CustomLink href={`/video/?id=${r?.sHashId}`}>
                  <ReelCard data={r} styles={styles} isCurrent={r?._id === activeReel?._id} />
                </CustomLink>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>

      <button ref={prevRef} className={`${styles.arrow} ${styles.prev} light-bg position-absolute translate-middle-y start-0`}></button>

      <button ref={nextRef} className={`${styles.arrow} ${styles.next} light-bg position-absolute translate-middle-y end-0`}></button>
    </>
  )
}

ReelSwiper.propTypes = {
  isArticle: PropTypes.bool
}

export default ReelSwiper
